import * as React from "react";
import Dropzone from "react-dropzone";
declare const I18n;

import { Event, Category } from "../../../../types";

import Header from "../../../Header";
import BreadCrumb from "../../../BreadCrumb";
import TextInput from "../../../inputs/TextInput";
import SelectInput from "../../../inputs/SelectInput";
import MultiSelectInput from "../../../inputs/MultiSelectInput";
import NoteInput from "../../../inputs/NoteInput";
import CheckboxInput from "../../../onboarding/inputs/CheckboxInput";
import Datepicker from 'vanillajs-datepicker/Datepicker';
import 'vanillajs-datepicker/css/datepicker.css';
import api from "../../../../api";
import { time } from "console";

type Props = {
  initialData: {
    event?: Event;
    eventCategories?: Category[];
    allCategories: Category[];
    locations: Array<{ title: string; url: string | null }>;
    userType?: string;
  };
};

const CreateEvent: React.FunctionComponent<Props> = ({
  initialData: { event, eventCategories, allCategories, locations, userType },
}: Props): JSX.Element => {
  const isAdmin = userType === "Admin";
  const [title, setTitle] = React.useState(event?.title || "");
  const [errorTitle, setErrorTitle] = React.useState("");
  const [facilitatorsOnly, setFacilitatorsOnly] = React.useState(event?.facilitators_only || false);

  const [description, setDescription] = React.useState(event?.description || "");
  const [errorDescription, setErrorDescription] = React.useState("");

  const [eventDate, setEventDate] = React.useState(
    event?.date ? event.date.split('T')[0] : ""
  );
  const [errorEventDate, setErrorEventDate] = React.useState("");

  const [startTime, setStartTime] = React.useState(event?.start_time || "");
  const [errorStartTime, setErrorStartTime] = React.useState("");

  const [endTime, setEndTime] = React.useState(event?.end_time || "");
  const [errorEndTime, setErrorEndTime] = React.useState("");

  const [location, setLocation] = React.useState(event?.location || "");
  const [errorLocation, setErrorLocation] = React.useState("");

  const [hostedBy, setHostedBy] = React.useState(event?.hosted_by || "");
  const [errorHostedBy, setErrorHostedBy] = React.useState("");
  const [language, setLanguage] = React.useState(event?.language || "en");

  const [photo, setPhoto] = React.useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = React.useState(event?.photo_url || null);
  const [errorPhoto, setErrorPhoto] = React.useState("");

  const [selectedCategories, setSelectedCategories] = React.useState([]);

  const dateInputRef = React.useRef<HTMLInputElement>(null);
  const datepickerInstance = React.useRef<Datepicker | null>(null);

  const [loading, setLoading] = React.useState(false);


  const getTZAbbr = (dateStr?: string) => {
    const date = dateStr
      ? new Date(`${dateStr}T12:00:00`)
      : new Date();

    return new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short'
    })
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value;
  };

  const tzAbbr = getTZAbbr(eventDate);

  React.useEffect(() => {
    if (dateInputRef.current) {
      datepickerInstance.current = new Datepicker(dateInputRef.current, {
        format: 'yyyy-mm-dd',
        autohide: true,
        todayHighlight: true,
        buttonClass: 'btn',
      });

      dateInputRef.current.addEventListener('changeDate', (e: any) => {
        const selectedDate = e.target.value;
        setEventDate(selectedDate);
        setErrorEventDate("");
      });
    }

    return () => {
      if (datepickerInstance.current) {
        datepickerInstance.current.destroy();
      }
    };
  }, []);

  React.useEffect(() => {
    if (event?.date && dateInputRef.current) {
      dateInputRef.current.value = event.date.split('T')[0];
    }
  }, [event?.date]);

  React.useEffect(() => {
    if (eventCategories) {
      setSelectedCategories(eventCategories.map((c) => c.id));
    }
  }, []);

  const handlePhotoDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
      setErrorPhoto("");
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    let error = false;

    if (!(title && title.trim())) {
      setErrorTitle("This field can't be blank");
      setTitle("");
      error = true;
    }

    if (!(description && description.trim())) {
      setErrorDescription("This field can't be blank");
      setDescription("");
      error = true;
    }

    if (!(eventDate && eventDate.trim())) {
      setErrorEventDate("This field can't be blank");
      setEventDate("");
      error = true;
    }

    if (error) {
      scrollOnError();
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("event[title]", title.trim());
    formData.append("event[description]", description.trim());
    formData.append("event[date]", eventDate);
    formData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone)

    if (startTime) {
      formData.append("event[start_time]", startTime);
    }
    if (endTime) {
      formData.append("event[end_time]", endTime);
    }
    if (location && location.trim()) formData.append("event[location]", location.trim());
    if (hostedBy && hostedBy.trim()) formData.append("event[hosted_by]", hostedBy.trim());
    if (isAdmin) formData.append("event[language]", language);

    formData.append("event[facilitators_only]", facilitatorsOnly.toString());

    selectedCategories.forEach((id) => {
      formData.append("event[category_ids][]", id);
    });

    if (photo) {
      formData.append("event[photo]", photo);
    }

    if (event) {
      api
        .updateEvent(event.slug, formData)
        .then((res) => (window.location.href = res.url))
        .catch((error) => {
          console.error("Error: ", error);
          setLoading(false);
        });
    } else {
      api
        .createEvent(formData)
        .then((res) => (window.location.href = res.url))
        .catch((error) => {
          console.error("Error: ", error);
          setLoading(false);
        });
    }
  };

  const scrollOnError = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  return (
    <div className="events_new watch_form_changes">
      <div className="events_new__container">
        <Header
          title={
            event
              ? I18n.t("models.event.messaging.edit_event", { name: event.title })
              : I18n.t("models.event.messaging.create")
          }
        />
        <BreadCrumb locations={locations} />
        <div className="col-md-12" style={{ marginTop: "60px" }}>
          <div className="row">
            <TextInput
              columnSize={8}
              error={errorTitle}
              value={title}
              type="event"
              field="title"
              label={I18n.t("models.event.attributes.title")}
              style={{ paddingLeft: 0 }}
              className="required"
              onChange={(value) => {
                setTitle(value);
                setErrorTitle("");
              }}
            />

            <div className="col-md-4" style={{ paddingRight: 0 }}>
              <div className="form-group required">
                <label>
                  {I18n.t("models.event.attributes.date")}
                </label>
                <input
                  ref={dateInputRef}
                  type="text"
                  className={`${errorEventDate ? 'is-invalid' : ''}`}
                  placeholder="Select date" // TODO: Add translation
                  readOnly
                />
                {errorEventDate && (
                  <div className="invalid-feedback d-block">
                    {errorEventDate}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row" style={{ marginTop: "20px" }}>
            <div className="col-md-4" style={{ paddingLeft: 0 }}>
              <div className="form-group">
                <label>{I18n.t("models.event.attributes.start_time")} {` (${tzAbbr})`}</label>
                <input
                  type="time"
                  className={`form-control ${errorStartTime ? 'is-invalid' : ''}`}
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    setErrorStartTime("");
                  }}
                />
                {errorStartTime && (
                  <div className="invalid-feedback d-block">{errorStartTime}</div>
                )}
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label>{I18n.t("models.event.attributes.end_time")} {` (${tzAbbr})`}</label>
                <input
                  type="time"
                  className={`form-control ${errorEndTime ? 'is-invalid' : ''}`}
                  value={endTime}
                  onChange={(e) => {
                    setEndTime(e.target.value);
                    setErrorEndTime("");
                  }}
                />
                {errorEndTime && (
                  <div className="invalid-feedback d-block">{errorEndTime}</div>
                )}
              </div>
            </div>

            <TextInput
              columnSize={4}
              error={errorLocation}
              value={location}
              type="event"
              field="location"
              label={I18n.t("models.event.attributes.location")}
              style={{ paddingRight: 0 }}
              onChange={(value) => {
                setLocation(value);
                setErrorLocation("");
              }}
            />
          </div>

          <div className="row" style={{ marginTop: "20px" }}>
            <TextInput
              columnSize={6}
              error={errorHostedBy}
              value={hostedBy}
              type="event"
              field="hosted_by"
              label={I18n.t("models.event.attributes.hosted_by")}
              style={{ paddingLeft: 0 }}
              onChange={(value) => {
                setHostedBy(value);
                setErrorHostedBy("");
              }}
            />

            {isAdmin && (
              <SelectInput
                columnSize={6}
                value={language}
                type="event"
                field="language"
                label={I18n.t("models.common.language")}
                style={{ paddingRight: 0 }}
                onChange={(value) => setLanguage(value)}
              >
                <option value={"en"}>{I18n.t("views.common.english")}</option>
                <option value={"fr"}>{I18n.t("views.common.french")}</option>
              </SelectInput>
            )}

            <MultiSelectInput
              value={selectedCategories}
              type="event"
              field="category_ids"
              label={I18n.t("models.event.attributes.categories") || "Event Categories"}
              columnSize={isAdmin ? 12 : 6}
              style={{ paddingRight: 0 }}
              onChange={(values) => setSelectedCategories(values)}
            >
              <>
                {allCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </>
            </MultiSelectInput>

            <div className="row" style={{ marginTop: "20px" }}>
              <div className="col-md-12" style={{ paddingLeft: 0 }}>
                <div className="form-group">
                  <CheckboxInput
                    label={I18n.t("models.event.attributes.facilitators_only")}
                    name="facilitators_only"
                    value="facilitators_only"
                    checked={facilitatorsOnly}
                    onChange={(checked) => setFacilitatorsOnly(checked)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <NoteInput
          error={errorDescription}
          value={description}
          type="event"
          field="description"
          label={I18n.t("models.event.attributes.description")}
          style={{ padding: 0, marginTop: "30px" }}
          required
          onChange={(text) => {
            setDescription(text);
            setErrorDescription("");
          }}
          toolbarOptions={[
            ["style", ["style"]],
            ["font", ["bold", "italic", "underline", "clear"]],
            ["para", ["ul", "ol", "paragraph"]],
            ["table", ["table"]],
            ["insert", ["link", "picture", "video"]],
            ["view", ["codeview"]],
          ]}
        />

        <div style={{ marginTop: "30px" }}>
          <label className="form-label">
            {I18n.t("models.event.attributes.photo")}
          </label>
          <Dropzone
            onDrop={handlePhotoDrop}
            accept={{ "image/*": [".png", ".jpg", ".jpeg", ".gif"] }}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <div
                {...getRootProps()}
                style={{
                  border: "2px dashed #ccc",
                  borderRadius: "4px",
                  padding: "40px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <input {...getInputProps()} />
                {photoPreview ? (
                  <div>
                    <img
                      src={photoPreview}
                      alt="Event photo preview"
                      style={{ maxWidth: "100%", maxHeight: "300px" }}
                    />
                    <p style={{ marginTop: "10px", color: "#666" }}>
                      Click or drag to replace photo
                    </p>
                  </div>
                ) : (
                  <div>
                    <i className="far fa-image fa-3x" style={{ color: "#ccc" }} />
                    <p style={{ marginTop: "10px", color: "#666" }}>
                      Drag & drop an event photo here, or click to select
                    </p>
                  </div>
                )}
              </div>
            )}
          </Dropzone>
          {errorPhoto && <div className="text-danger mt-2">{errorPhoto}</div>}
        </div>

        <div
          className="col-12 text-center"
          style={{ paddingTop: "40px", borderTop: "solid 1px #CDCDCD", marginTop: "40px" }}
        >
          <button
            type="submit"
            name="commit"
            disabled={loading}
            className="btn btn-primary pull-right"
            onClick={handleSubmit}
          >
            {I18n.t("views.forms.submit")}
            {loading && (
              <i className="far fa-spinner-third fa-spin btn-loader ml-2"></i>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEvent;